import { Link } from "react-router-dom";
import React from "react";

import {
  FiActivity,
  FiArrowUpRight,
  FiBarChart2,
  FiCode,
  FiCloud,
  FiCpu,
  FiLayers,
  FiLock,
  FiMonitor,
  FiZap,
} from "react-icons/fi";

import "../styles/Technology.css";

const capabilities = [
  {
    icon: <FiCode />,
    title: "Product Engineering",
    text: "Web platforms, customer portals, SaaS products, and software that are designed to perform.",
  },
  {
    icon: <FiCloud />,
    title: "Cloud & Integrations",
    text: "Connected systems and dependable cloud foundations that give teams room to move faster.",
  },
  {
    icon: <FiCpu />,
    title: "Intelligent Automation",
    text: "Practical AI and automation that reduce friction in your workflows and service delivery.",
  },
  {
    icon: <FiLock />,
    title: "Secure by Design",
    text: "Thoughtful architecture and safeguards that protect your data, users, and momentum.",
  },
];

const serviceLines = [
  [
    "Digital Products",
    "From initial concept to a polished product experience.",
  ],
  [
    "Technology Growth",
    "Clear positioning and demand generation for ambitious technology brands.",
  ],
  [
    "Customer Platforms",
    "Useful portals, dashboards, and CRM journeys built around real users.",
  ],
  [
    "Mobile Experiences",
    "Mobile apps that make your services available wherever work happens.",
  ],
  [
    "Data Visibility",
    "Dashboards and reporting that turn activity into confident decisions.",
  ],
  [
    "System Modernisation",
    "Modernise legacy processes without losing the knowledge that powers them.",
  ],
];

const delivery = [
  {
    title: "Explore",
    text: "We learn what your customers need, where your operations slow down, and what success looks like.",
  },
  {
    title: "Architect",
    text: "We turn priorities into a practical technical roadmap, designed for the way your organisation works.",
  },
  {
    title: "Launch",
    text: "Our team designs, builds, tests, and ships a reliable digital experience with your goals in view.",
  },
  {
    title: "Evolve",
    text: "We refine what is live, add what is valuable, and help your technology keep pace with growth.",
  },
];

const technologyStack = [
  "React",
  "JavaScript",
  "Node.js",
  "Python",
  "Java",
  "Cloud",
  "AI & ML",
  "APIs",
  "Databases",
  "Cybersecurity",
  "Automation",
  "Analytics",
];

function Technology() {
  return (
    <main className="technology-page">

      {/* HERO */}
      <section className="technology-hero">
        <div className="tech-grid" />
        <div className="tech-pulse pulse-a" />
        <div className="tech-pulse pulse-b" />

        <div className="technology-hero-copy reveal-tech">
          <span className="tech-overline">
            TECHLYNX FOR IT & TECHNOLOGY
          </span>

          <h1>
            Technology that <em>moves</em> your business forward
          </h1>

          <p>
            We build secure, scalable digital products that give technology
            organisations the confidence to create, connect, and grow.
          </p>

          <div className="tech-hero-actions">
            <a className="tech-button" href="#tech-solutions">
              See what's possible
              <FiArrowUpRight />
            </a>

            <span className="tech-hero-note">
              Strategy • Engineering • Innovation
            </span>
          </div>
        </div>

        <div className="tech-hero-image">
          <img
            src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=85"
            alt="Modern technology circuit board"
          />

          <div className="tech-image-overlay" />

          <div className="tech-image-card">
            <span>TECHNOLOGY</span>
            <strong>ENGINEERED FOR CHANGE</strong>
          </div>
        </div>

        {/*<div className="tech-sphere" aria-hidden="true">
          <span />
          <span />
          <span />
          <FiActivity />
        </div>*/}
      </section>

      {/* INTRODUCTION */}
      <section className="technology-intro">
        <div className="technology-intro-image reveal-tech">
          <img
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=85"
            alt="Technology team collaborating"
          />
        </div>

        <div className="technology-intro-content reveal-tech">
          <span className="tech-overline">
            TECHNOLOGY THAT WORKS FOR YOU
          </span>

          <h2>
            Transform complexity into <em>clarity</em>
          </h2>

          <p>
            Technology should make business simpler, faster, and more
            connected. Techlynx combines engineering, design, cloud,
            automation, data, and security to create digital systems built
            around real business needs.
          </p>

          <p>
            From a new digital product to modernising an existing platform,
            we focus on creating technology that is practical today and ready
            for tomorrow.
          </p>

          <div className="intro-stats">
            <div>
              <strong>01</strong>
              <span>Strategy</span>
            </div>

            <div>
              <strong>02</strong>
              <span>Engineering</span>
            </div>

            <div>
              <strong>03</strong>
              <span>Innovation</span>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="tech-capabilities" id="tech-solutions">
        <div className="tech-section-heading">
          <div>
            <span className="tech-overline">CORE CAPABILITIES</span>

            <h2>
              Built for complex ideas, <em>made clear</em>
            </h2>
          </div>

          <p>
            From first roadmap to continuous improvement, Techlynx brings the
            strategy, design, and technical craft to move your product ahead.
          </p>
        </div>

        <div className="tech-capability-grid">
          {capabilities.map((item, index) => (
            <article
              className="tech-capability-card reveal-tech"
              style={{ "--delay": `${index * 100}ms` }}
              key={item.title}
            >
              <div className="capability-icon">{item.icon}</div>

              <span className="capability-number">
                0{index + 1}
              </span>

              <h3>{item.title}</h3>

              <p>{item.text}</p>

              <div className="capability-line" />
            </article>
          ))}
        </div>
      </section>

      {/* VISUAL TECHNOLOGY SECTION */}
      <section className="technology-showcase">
        <div className="showcase-content reveal-tech">
          <span className="tech-overline">
            DIGITAL TRANSFORMATION
          </span>

          <h2>
            Build systems that are <em>ready for what comes next</em>
          </h2>

          <p>
            Modern organisations need technology that can evolve with their
            customers, teams, data, and market. We design connected
            experiences that bring these moving parts together.
          </p>

          <div className="showcase-points">
            <span>
              <FiZap />
              Faster digital delivery
            </span>

            <span>
              <FiLayers />
              Scalable architecture
            </span>

            <span>
              <FiLock />
              Security-focused systems
            </span>
          </div>
        </div>

        <div className="showcase-image reveal-tech">
          <img
            src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1400&q=85"
            alt="Cybersecurity technology environment"
          />

          <div className="showcase-image-glow" />

          <div className="showcase-floating-card">
            <FiCpu />
            <div>
              <span>INTELLIGENT SYSTEMS</span>
              <strong>CONNECTED • SECURE • SCALABLE</strong>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="tech-services">
        <div className="tech-section-heading">
          <div>
            <span className="tech-overline">
              WHERE WE ADD VALUE
            </span>

            <h2>
              One team for every <em>important</em> digital move
            </h2>
          </div>

          <p>
            Technology is rarely one isolated project. Our capabilities
            connect across products, platforms, data, infrastructure, and
            customer experiences.
          </p>
        </div>

        <div className="tech-service-lines">
          {serviceLines.map(([title, text], index) => (
            <article
              className="tech-service-line reveal-tech"
              style={{ "--delay": `${index * 75}ms` }}
              key={title}
            >
              <span className="service-number">
                0{index + 1}
              </span>

              <div className="service-content">
                <h3>{title}</h3>
                <p>{text}</p>
              </div>

              <FiArrowUpRight className="service-arrow" />
            </article>
          ))}
        </div>
      </section>

      {/* TECHNOLOGY STACK */}
      <section className="technology-stack-section">
        <div className="stack-image reveal-tech">
          <img
            src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=85"
            alt="Developers working with modern technology"
          />

          <div className="stack-image-overlay" />
        </div>

        <div className="stack-content reveal-tech">
          <span className="tech-overline">
            TECHNOLOGY STACK
          </span>

          <h2>
            The tools behind <em>digital progress</em>
          </h2>

          <p>
            We select technologies based on the problem, product, scale, and
            long-term requirements rather than forcing every project into the
            same technical stack.
          </p>

          <div className="technology-tags">
            {technologyStack.map((technology) => (
              <span key={technology}>{technology}</span>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERY */}
      <section className="tech-delivery">
        <div className="tech-delivery-intro">
          <span className="tech-overline">
            THE TECHLYNX APPROACH
          </span>

          <h2>
            A calm path through <em>complexity</em>
          </h2>

          <p>
            Every project starts with understanding the problem before
            choosing the technology. Our process keeps strategy, engineering,
            communication, and measurable outcomes connected.
          </p>
        </div>

        <div className="tech-delivery-steps">
          {delivery.map((item, index) => (
            <article
              className="tech-delivery-step reveal-tech"
              style={{ "--delay": `${index * 100}ms` }}
              key={item.title}
            >
              <b>0{index + 1}</b>

              <div className="delivery-step-line" />

              <h3>{item.title}</h3>

              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="tech-outcomes">
        <div className="outcomes-visual">
          <div className="outcomes-ring ring-one" />
          <div className="outcomes-ring ring-two" />
          <div className="outcomes-ring ring-three" />

          <div className="outcomes-core">
            <FiMonitor />
          </div>

          <div className="outcome-icon outcome-one">
            <FiLayers />
          </div>

          <div className="outcome-icon outcome-two">
            <FiBarChart2 />
          </div>

          <div className="outcome-icon outcome-three">
            <FiZap />
          </div>
        </div>

        <div className="tech-outcomes-content">
          <span className="tech-overline">
            TECHNOLOGY WITH MOMENTUM
          </span>

          <h2>
            Turn your next big challenge into a stronger digital advantage
          </h2>

          <p>
            Whether you are launching a new platform, improving a customer
            experience, or connecting the systems behind it all, we build
            with purpose and scale in mind.
          </p>

          <Link className="tech-button" to="/contact">
            Start a project
            <FiArrowUpRight />
          </Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="technology-final-cta">
        <div className="final-cta-background">
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1800&q=85"
            alt=""
          />
        </div>

        <div className="final-cta-overlay" />

        <div className="final-cta-content reveal-tech">
          <span className="tech-overline">
            READY FOR THE NEXT MOVE?
          </span>

          <h2>
            Let's turn your technology vision into <em>impact</em>
          </h2>

          <p>
            Bring us your challenge, idea, or transformation roadmap.
            Together, we can build what comes next.
          </p>

          <Link className="tech-button tech-button-light" to="/contact">
            Talk to Techlynx
            <FiArrowUpRight />
          </Link>
        </div>
      </section>

    </main>
  );
}

export default Technology;